/**
 * ReleaseUndoSnackbar
 *
 * Global, bottom-centre "Released · Undo" snackbar with a visible countdown
 * (progress bar + seconds). Mounted once in App so it survives the drawer
 * closing. Fed by announceRelease() from the release click paths, so only the
 * releaser ever sees it. Keyboard: Tab reaches Undo, Esc dismisses.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useUndoRelease, useUndoReleaseBulk } from '@/hooks/useApproval';
import { formatApiError } from '@/services/api';
import { useReleaseUndoStore, type ReleaseUndoEntry } from '@/stores/releaseUndo.store';
import { useReleaseMomentStore } from '@/stores/releaseMoment.store';
import { toast } from '@/utils/toast';

const TICK_MS = 100;

/** Translated reason for one failed item of a bulk undo (by API error code). */
const bulkFailureReason = (t: TFunction, code: string): string => {
  switch (code) {
    case 'release.undo_expired':
      return t('release_undo_bulk_reason_expired');
    case 'release.undo_scheduled':
      return t('release_undo_bulk_reason_scheduled');
    case 'release.undo_not_allowed':
      return t('release_undo_bulk_reason_not_allowed');
    default:
      return t('release_undo_bulk_reason_other');
  }
};

const Countdown = ({ entry }: { entry: ReleaseUndoEntry }) => {
  const { t } = useTranslation('common');
  const dismiss = useReleaseUndoStore((s) => s.dismiss);
  const undo = useUndoRelease();
  const undoBulk = useUndoReleaseBulk();
  const bulkItems = entry.bulkItems;
  // Fixed at mount (the component is keyed by token) so the bar drains from 100%.
  const [totalMs] = useState(() => Math.max(1, entry.undoUntilMs - Date.now()));
  const [remainingMs, setRemainingMs] = useState(totalMs);

  useEffect(() => {
    const id = window.setInterval(() => {
      const left = entry.undoUntilMs - Date.now();
      if (left <= 0) {
        window.clearInterval(id);
        dismiss();
        return;
      }
      setRemainingMs(left);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [entry.undoUntilMs, dismiss]);

  const handleUndo = useCallback(async (): Promise<void> => {
    if (undo.isPending || undoBulk.isPending) return;
    if (bulkItems) {
      try {
        const result = await undoBulk.mutateAsync(bulkItems);
        if (result.undone.length > 0) useReleaseMomentStore.getState().dismiss();
        if (result.failed.length === 0) {
          toast.success(t('release_undo_bulk_done', { count: result.undone.length }));
        } else {
          const reasons = [...new Set(result.failed.map((f) => bulkFailureReason(t, f.code)))].join(' ');
          const message = t('release_undo_bulk_partial', {
            done: result.undone.length,
            failed: result.failed.length,
            reasons,
          });
          if (result.undone.length > 0) toast.warning(message);
          else toast.error(message);
        }
      } catch (error) {
        toast.error(formatApiError(error).message || t('release_undo_failed'));
      } finally {
        dismiss();
      }
      return;
    }
    try {
      const resultingStatus = await undo.mutateAsync({ postId: entry.postId, variationId: entry.variationId });
      useReleaseMomentStore.getState().dismiss();
      // The copy follows the server's resulting state: a DIRECT release goes back to Draft, not to review
      toast.success(t(resultingStatus === 'DRAFT' ? 'release_undo_done_draft' : 'release_undo_done'));
    } catch (error) {
      toast.error(formatApiError(error).message || t('release_undo_failed'));
    } finally {
      dismiss();
    }
  }, [undo, undoBulk, bulkItems, entry.postId, entry.variationId, dismiss, t]);

  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  const percent = Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));

  return (
    <div
      role="status"
      aria-live="polite"
      onKeyDown={(e) => {
        if (e.key === 'Escape') dismiss();
      }}
      className="pointer-events-auto w-[min(92vw,360px)] overflow-hidden rounded-xl border border-primary-200 bg-white text-gray-900 shadow-xl"
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5 sm:gap-3 sm:px-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
          <Icon name={ICONS.RELEASE_STAMP} size="sm" ariaHidden />
        </span>
        <span className="min-w-0 flex-1 text-sm font-medium leading-snug">{bulkItems
            ? entry.summary ?? t('release_undo_bulk_released', { count: bulkItems.length })
            : t('release_released_toast')}</span>
        <span className="shrink-0 text-xs tabular-nums text-gray-500" aria-hidden="true">
          {`${seconds}s`}
        </span>
        {entry.onDetails && (
          <button
            type="button"
            onClick={entry.onDetails}
            className="h-[44px] rounded-md px-2 text-xs font-semibold text-primary-700 underline underline-offset-2 transition-colors hover:text-primary-900 focus:outline-none focus-visible:bg-primary-50 sm:h-8"
          >
            {t('release_bulk_details')}
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleUndo()}
          aria-disabled={undo.isPending || undoBulk.isPending}
          aria-label={t('release_undo_aria', { seconds })}
          className="h-[44px] shrink-0 rounded-md bg-primary-600 px-3 text-xs font-semibold text-primary-contrast transition-colors hover:bg-primary-700 focus:outline-none focus-visible:bg-primary-800 aria-disabled:cursor-wait aria-disabled:opacity-60 sm:h-8"
        >
          {t('release_undo')}
        </button>
      </div>
      <div className="h-1 bg-primary-50" aria-hidden="true">
        <div
          className="h-full bg-primary-500 transition-[width] duration-100 ease-linear motion-reduce:transition-none"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const BELOW_LG = '(max-width: 1023px)';
const COMPOSER_GAP_PX = 8;

/**
 * Below lg the release drawer is full screen and its chat composer is pinned to the bottom: the
 * snackbar must sit above it (returns the px offset from the viewport bottom), else null.
 */
const useDrawerComposerOffset = (active: boolean): number | null => {
  const [offset, setOffset] = useState<number | null>(null);
  useEffect(() => {
    if (!active) {
      setOffset(null);
      return;
    }
    const update = () => {
      const composer = document.querySelector<HTMLElement>('[data-release-composer]');
      const narrow = window.matchMedia(BELOW_LG).matches;
      if (!composer || !narrow) {
        setOffset((prev) => (prev === null ? prev : null));
        return;
      }
      const next = Math.max(0, Math.round(window.innerHeight - composer.getBoundingClientRect().top)) + COMPOSER_GAP_PX;
      setOffset((prev) => (prev === next ? prev : next));
    };
    update();
    const id = window.setInterval(update, 200);
    window.addEventListener('resize', update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', update);
    };
  }, [active]);
  return offset;
};

export const ReleaseUndoSnackbar = () => {
  const entry = useReleaseUndoStore((s) => s.entry);
  const composerOffset = useDrawerComposerOffset(entry !== null);
  if (!entry) return null;
  return (
    <div
      // Below lg: above the bottom nav (64px + safe area), or above the drawer composer when it is open.
      className="pointer-events-none fixed inset-x-0 z-toast flex justify-center px-3 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+12px+var(--floating-stack-offset))] lg:bottom-[max(1.5rem,env(safe-area-inset-bottom))]"
      style={composerOffset !== null ? { bottom: composerOffset } : undefined}
    >
      <Countdown key={entry.token} entry={entry} />
    </div>
  );
};

export default ReleaseUndoSnackbar;
